import React, { memo } from "react";
import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import Icon, { type IconName } from "../AppIcon";
import { useAuth } from "../../contexts/AuthContext";
import { teacherQueries } from "../../api/queries/teacher";

interface TeacherNavigationItem {
  label: string;
  path: string;
  icon: IconName;
  badge?: number;
}

const teacherNavigationData: TeacherNavigationItem[] = [
  {
    label: "Overview",
    path: "/teacher/overview",
    icon: "LayoutDashboard",
  },
  {
    label: "My Courses",
    path: "/teacher/my-courses",
    icon: "BookOpen",
  },
  {
    label: "Create Course",
    path: "/teacher/create-course",
    icon: "Plus",
  },
  {
    label: "MCQ Management",
    path: "/teacher/mcq",
    icon: "HelpCircle",
  },
  {
    label: "Coupons",
    path: "/teacher/coupons",
    icon: "Tag",
  },
  {
    label: "Security",
    path: "/teacher/security",
    icon: "Shield",
  },
  {
    label: "Analytics",
    path: "/teacher/analytics",
    icon: "BarChart3",
  },
  {
    label: "Enrolled Students",
    path: "/teacher/students",
    icon: "Users",
  },
  {
    label: "Enrollment Audit",
    path: "/teacher/audit/enrollments",
    icon: "ClipboardList",
  },
  {
    label: "Orders",
    path: "/teacher/orders",
    icon: "ShoppingCart",
  },
  {
    label: "Live Session",
    path: "/teacher/live-session",
    icon: "Radio",
  },
  {
    label: "Broadcasts",
    path: "/teacher/broadcasts",
    icon: "Megaphone",
  },
];

interface TeacherSidebarProps {
  isCollapsed?: boolean;
}

const TeacherSidebar: React.FC<TeacherSidebarProps> = memo(
  ({ isCollapsed = false }) => {
    const location = useLocation();
    const { user } = useAuth();

    const { data: teacherProfile } = useQuery({
      ...teacherQueries.profile(),
      enabled: !!user && user.role === "TEACHER",
    });

    // Exact match, or prefix match so drill-down routes (e.g.
    // /teacher/students/:id/enrollments) keep their parent entry highlighted.
    const isActive = (path: string) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`);

    return (
      <aside
        className={`fixed left-0 top-0 z-1000 h-screen bg-card border-r border-border transition-all duration-300 ${
          isCollapsed ? "w-20" : "w-[280px]"
        } lg:fixed`}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center px-6 border-b border-border">
            <Link to="/teacher/overview" className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <Icon name="GraduationCap" size={24} color="white" />
              </div>
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-lg font-semibold text-foreground">
                    EduTech
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Teacher Portal
                  </span>
                </div>
              )}
            </Link>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <ul className="space-y-1">
              {teacherNavigationData.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    title={item.label}
                    className={`group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                      isActive(item.path)
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {isActive(item.path) && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1 bg-primary rounded-r-full" />
                    )}
                    <Icon
                      name={item.icon}
                      size={20}
                      className={
                        isActive(item.path)
                          ? "text-primary"
                          : "text-muted-foreground"
                      }
                    />
                    {!isCollapsed && (
                      <span className="flex-1">{item.label}</span>
                    )}
                    {!isCollapsed && item.badge && (
                      <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Analytics Overview Widget */}

          {!isCollapsed && (
            <div className="border-t border-border p-4">
              <Link
                to="/teacher/profile"
                className="flex items-center gap-3 rounded-lg bg-muted p-3 hover:bg-muted/80 transition-colors cursor-pointer"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-medium">
                  {teacherProfile?.firstName?.charAt(0).toUpperCase() ||
                    user?.firstName?.charAt(0).toUpperCase() ||
                    "T"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {teacherProfile?.firstName && teacherProfile?.lastName
                      ? `${teacherProfile.firstName} ${teacherProfile.lastName}`
                      : user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : "Teacher"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    View Profile
                  </p>
                </div>
                <Icon
                  name="ChevronRight"
                  size={16}
                  className="text-muted-foreground"
                />
              </Link>
            </div>
          )}
        </div>
      </aside>
    );
  },
);

TeacherSidebar.displayName = "TeacherSidebar";

export default TeacherSidebar;
